import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from "axios"
import { KJUR, KEYUTIL, hextob64 } from "jsrsasign"
import { Dispatch } from "react"
import { setData } from "../app/slices/patients/patientSlice"

const privateKeyPem =
  "-----BEGIN PRIVATE KEY-----\n" +
  "MIIEvQIBADANBgkqhkiG9w0BAQEFAASCBKcwggSjAgEAAoIBAQCJPeA+zGMmuDt1MsE6Xc+c38H5VU+2K9un4ebguxuu4Oy9HbhW1HznK05ArZ1GVv76YusGie0sx2dcoUU7ZNXc7Tr7BmiQj3d1+2RtCKVTSPWu2lwbKuKgQQUTfIPDchVbXIkM38+wXQaU+Pe3DGysFvw/eJfmSpVAQpagUci3VXEpin8gv4qUWeGMVdiynTAejKsfEt9imo6DCz3vGPajF36ATCSUHBf6D3AIoJ4lRPHhNgFNjwpme19E28+2go/4fuGJoiWVrId6VupaPhutyIiz1VpBWDl3023PWrDvvU6U8n/0LR7jrXyWCL8gAbpCm8ucmAK+oaOMBxS7OFKpAgMBAAECggEALOkIpsQvdg43tsRjIvxr4gP+796Q9SYo/SkZalUQXAvSrJg7KH7kWKl9NAEcRFZXngwcqMkdydd+5XsIssTujxs6f8a7FQM9rmgTlvKLV1O6wftGxMUl4Bcm2BeY7q44eusvFf1vc2EdtEY8mbQBI9dpuEN4Qa4KZTrlG5Vnv0ZF0MBjcTRAa8h9QzC1JxKvIw+Q8lwCcvMzq7yvA9hOE4wly8ujGN7Xnb5I6PlBSfQeSZFRvBU/0Tq3q5sw3d4iM1yqaYxjho/U0CL6dNIRdMy5+RZGz+qPerhgUMKtFgCEY9IZ76CmrMP1IFb2pZdY1JFR+lA/M46QkJBkEE2ZmQKBgQC9h0O+wVggo8kWOnVMGaDb2kHRFUqf+c1VpMKTTZZwRK6zNN7aagv53jba71MKprZ4ZjzLIr+VI4TbuycQuMqCEPK9ECZ8YJMm6sOGfI7IaP0VQXYfLVWhUENs5gixg+UdvmjcKImmXwavgHteBoo852UIxvhj2QS2QPHROZopIwKBgQC5YBJScCrHT2fivnTBH3IMh1iICD4s2Q+bq9WDfsgL8fKN8Hlb99tZtwcUvNZe3KB4+5ZDNaMsUjAomSqCrEXpjO+CJzpUQK+HF/5o599tiyYjhOUqJjWk3TJt5/i2DHL17+LJteHlv5CZg1LOB4+3d2g+xLG5EKkTydeXAk1fwwKBgQCAr2EOGhUORGb6ksKl4xjXbgAWSNCcgm4q4m3CuoGUxO2hfBp8TljHVVwFl03ZA40Nfs10V6mhR7ajwxUpQ28slPlb6aVX+fKwD+KmY9hGtxJv5jyfupZTqz28qQvqC9X7NhfYtCWSMx9E9oXNN7+QzsRJC2AH24hZfwsxtly35QKBgCQ73Rj0B8iRgtLVO2kMRnSgd/OO+Og3PU/GVKOxQlTjY+/Ok0ogtmVg2yd4icZdBZjgREy6zTw6jGNDHQYRulCeVapxFHuncSfMztOejxOQYVIMBecpNM8W1WaaMwkSgd4El6WpwY/w08jN3H+dBsBrvJqYQ8zSrtnJWDSSJKGVAoGAZaQi5gsaq2gLtgvmcdFdBkx3kxkdI2798tvuxpb/ywMdzkTOU4MkCZNveGQn+oWM8D+m/49nxMytG7nfby+wmb/XkG1jSQT4KMTXwPCW9H5dPGxQ+Rpg6H2Hap2HaV+MOK1JVDEbcGSGWv6PbA/Lv32Mkmcq+YunOK8owkWzpjo=" +
  "\n-----END PRIVATE KEY-----"
const devOpenId = "209af74e8f73429993ad93175ac0fea1"
const appId = "20241216110502952828928"

class HttpAlliedstar {
  private instance: AxiosInstance

  constructor() {
    this.instance = axios.create({
      timeout: 60000,
      //baseURL: "https://test-api.allied-star.com",
      baseURL: "https://api-us.allied-star.com",
    })
  }

  async get<T = any>(
    path: string,
    config?: AxiosRequestConfig,
  ): Promise<AxiosResponse<T>> {
    try {
      const response = await this.instance.get<T>(path, {
        ...config,
        headers: {
          ...(config && config.headers),
        },
      })
      return response
    } catch (error) {
      console.error("GET request failed:", error)
      throw error
    }
  }

  async post<T = any>(
    path: string,
    data: T,
    signature: string,
    config?: AxiosRequestConfig,
  ): Promise<AxiosResponse<T>> {
    try {
      const formData = new URLSearchParams({
        ...data,
        sign: signature,
      })

      const response = await this.instance.post(path, formData, {
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
          ...(config && config.headers),
        },
        ...config,
      })
      return response
    } catch (error) {
      console.error("POST request failed:", error)
      throw error
    }
  }
}

export default new HttpAlliedstar()

function signDataWithRSA(privateKeyPem: string, data: string) {
  try {
    var key = KEYUTIL.getKey(privateKeyPem)
  } catch {
    alert(
      "Please check the format of private key. Does it correspond to the tab you choose?",
    )
    return ""
  }

  let rsa = new KJUR.crypto.Signature({ alg: "SHA256withRSA" })
  rsa.init(key)
  rsa.updateString(data)

  let a = rsa.sign()
  let sign = hextob64(a)
  return sign
}

function objectToQueryString(obj: any) {
  // Get the keys of the object and sort them alphabetically
  var keys = Object.keys(obj).sort()

  // Create an array to store the key-value pairs
  var keyValuePairs = []

  // Iterate through the sorted keys and format the pairs
  for (var i = 0; i < keys.length; i++) {
    var key = keys[i]
    var value = obj[key]
    keyValuePairs.push(key + "=" + value) // Use encodeURIComponent to handle special characters
  }

  // Join the key-value pairs with "&" to create the final string
  var queryString = keyValuePairs.join("&")

  return queryString
}

export async function getPatientslist(
  patientName?: string, //
  patientId?: string, //
  startTime?: string, //
  endTime?: string, //
  pageNumber?: string, //
  pageSize?: string, //default is 10, maximum is 100
) {
  try {
    const alliedstarClient = new HttpAlliedstar()
    let jsonObj = JSON.parse(localStorage.getItem("alliesData"))

    const payload = {
      appid: appId,
      openid: jsonObj.openid,
      charset: "UTF-8",
      sign_type: "RSA2",
      ...(patientName && { patientName }),
      ...(patientId && { patientId }),
      ...(startTime && { startTime }),
      ...(endTime && { endTime }),
      ...(pageNumber && { pageNumber }),
      ...(pageSize && { pageSize }),
    }
    const content = objectToQueryString(payload)
    const signature = signDataWithRSA(privateKeyPem, content)
    const response = await alliedstarClient.post(
      "/open/patient/listPatients",
      payload,
      signature,
    )
    console.log(response)
    return response
  } catch (error) {
    console.error("Request error:", error)
    return null
  }
}

interface DataItem {
  Attachments: any[]
  Scans: any[]
  Info: {
    CaseId: string
    PatientName: string
    CreateTime: string
    FirstName: string
    LastName: string
  } //Record<string, unknown>;
}

function splitAtFirstSpace(str: string): string[] {
  // 1. 判断字符串中是否有空格
  const hasSpace = str.includes(" ")

  if (hasSpace) {
    // 2. 如果有空格，使用 indexOf 找到第一个空格的位置
    const firstSpaceIndex = str.indexOf(" ")

    // 3. 使用 slice 或 substring 按照第一个空格切分
    // slice(startIndex, endIndex) 截取从 startIndex 到 endIndex-1 的部分
    // slice(startIndex) 截取从 startIndex 到字符串末尾的部分
    const part1 = str.slice(0, firstSpaceIndex)
    const part2 = str.slice(firstSpaceIndex + 1) // +1 跳过空格本身

    return [part1, part2]
  } else {
    const hasPoint = str.includes(".")

    if (hasPoint) {
      const firstSpaceIndex = str.indexOf(".")
      const part1 = str.slice(0, firstSpaceIndex)
      const part2 = str.slice(firstSpaceIndex + 1) // +1 跳过空格本身

      return [part1, part2]
    } else {
      // 4. 如果没有空格，返回包含原字符串的单个元素的数组
      return [str, str]
    }
  }
}

export async function getAlliedCaselist(
  page?: number, //
  patientName?: string,
  caseStatus?: string, //Case status, multiple comma-separated(0: Registered, 1: To be processed, 2: Scan completed, 3: Uploaded, 4: Ordered, 5: Completed)
  patientId?: string, //
  startTime?: string, //
  endTime?: string, //
  pageSize?: string, //default is 10, maximum is 100
) {
  const result: DataItem[] = []

  try {
    const alliedstarClient = new HttpAlliedstar()
    let jsonObj = JSON.parse(localStorage.getItem("alliesData"))

    const pageNumber = (page + 1).toString()
    const payload = {
      appid: appId,
      openid: jsonObj.openid,
      charset: "UTF-8",
      sign_type: "RSA2",
      ...(caseStatus && { caseStatus }),
      ...(patientName && { patientName }),
      ...(patientId && { patientId }),
      ...(startTime && { startTime }),
      ...(endTime && { endTime }),
      ...(pageNumber && { pageNumber }),
      ...(pageSize && { pageSize }),
    }
    const content = objectToQueryString(payload)
    const signature = signDataWithRSA(privateKeyPem, content)
    const response = await alliedstarClient.post(
      "/open/order/listCases",
      payload,
      signature,
    )
    console.log(response)

    const result: DataItem[] = response.data.data.map((item: any) => {
      const namesArray = splitAtFirstSpace(item.patientName)
      return {
        Attachments: item.attachments || [], // 根据实际 API 字段名调整
        Scans: item.scans || [],
        Info: {
          CaseId: item.caseCode, // 示例字段
          PatientName: item.patientName,
          CreateTime: item.scanTime,
          LastName: namesArray[1],
          FirstName: namesArray[0],
          Comments: item.comment || "",
        },
      }
    })

    console.log(result)
    return {caseCount : response.data.total, result}
  } catch (error) {
    console.error("Request error:", error)
    return null
  }
}

export async function creatNewCase(
  externalCaseCode: string, //Developer-defined case code with a maximum length of 100
  caseName: string, //Case name with a maximum length of 200
  externalPatientCode: string, //Developer-defined unique patient identifier with a maximum length of 200
  patientName: string, //Patient name with a maximum length of 100
  treatmentsScan: number, //Category (0: Restoration, 1: Implant, 2: Orthodontics, 3: Other)
  treatmentsToothType: number, //Tooth type (0: Adult, 1: Child)
  birthDate?: string, //YYYY-MM-DD
  gender?: string, //"male" or "female"
  treatments?: string, //Treatment details, maximum of 32; null to keep existing data, empty array to delete old data JSON string; [{"number":"12","scan":"0","toothType":"0"}]
  treatmentsNum?: string, //Tooth position, maximum length of 20
) {
  try {
    const alliedstarClient = new HttpAlliedstar()
    let jsonObj = JSON.parse(localStorage.getItem("alliesData"))

    const payload = {
      appid: appId,
      openid: jsonObj.openid,
      charset: "UTF-8",
      sign_type: "RSA2",
      externalCaseCode: externalCaseCode,
      caseName: caseName,
      externalPatientCode: externalPatientCode,
      treatmentsScan: treatmentsScan,
      treatmentsToothType: treatmentsToothType,
      ...(birthDate && { birthDate }),
      ...(gender && { gender }),
      ...(treatments && { treatments }),
      ...(treatmentsNum && { treatmentsNum }),
    }
    const content = objectToQueryString(payload)
    const signature = signDataWithRSA(privateKeyPem, content)
    const response = await alliedstarClient.post(
      "/open/order/createCase",
      payload,
      signature,
    )
    console.log(response)
    return response
  } catch (error) {
    console.error("Request error:", error)
    return null
  }
}

export async function getAlliedCaseDetail(caseCode: string) {
  try {
    const alliedstarClient = new HttpAlliedstar()
    let jsonObj = JSON.parse(localStorage.getItem("alliesData"))

    const payload = {
      appid: appId,
      openid: jsonObj.openid,
      charset: "UTF-8",
      sign_type: "RSA2",
      caseCode: caseCode,
    }
    const content = objectToQueryString(payload)
    const signature = signDataWithRSA(privateKeyPem, content)
    const response = await alliedstarClient.post(
      "/open/order/getCaseDetail",
      payload,
      signature,
    )
    console.log("caseDetail=", response.data.data)
    return response
  } catch (error) {
    console.error("Request error:", error)
    return null
  }
}

export async function getShowCaseURL(caseCode: string) {
  try {
    const alliedstarClient = new HttpAlliedstar()
    let jsonObj = JSON.parse(localStorage.getItem("alliesData"))

    const payload = {
      appid: appId,
      openid: jsonObj.openid,
      charset: "UTF-8",
      sign_type: "RSA2",
      caseCode: caseCode,
    }
    const content = objectToQueryString(payload)
    const signature = signDataWithRSA(privateKeyPem, content)
    const response = await alliedstarClient.post(
      "/open/order/caseShow",
      payload,
      signature,
    )
    console.log(response)
    return response
  } catch (error) {
    console.error("Request error:", error)
    return null
  }
}

function getKeyName(fileName: string): string {
  return fileName.split(".")[0]
}

export async function downloadAlliedStarFilesbyName(
  caseCode: string,
  filesName?: string[],
) {
  if (!filesName || filesName.length === 0) {
    filesName = ["arch_u.stl", "arch_l.stl"]
  }

  const response = await getAlliedCaseDetail(caseCode)
  if (response.status != 200) return {}

  const attachments = response?.data?.data?.attachments || []
  const result: { [key: string]: File | null } = {}

  for (const name of filesName) {
    const matched = attachments.find((item) => item.fileName === name)
    if (!matched) {
      console.warn(`can't find: ${name}`)
      result[getKeyName(name)] = null
      continue
    }

    let url = matched.url

    try {
      const fetchRes = await fetch(url)
      if (!fetchRes.ok) {
        throw new Error(`Download failed: ${name} (${fetchRes.status})`)
      }

      const blob = await fetchRes.blob()
      const file = new File([blob], name, {
        type: blob.type || "application/octet-stream",
      })

      result[getKeyName(name)] = file
    } catch (error) {
      console.error(`Error downloading file ${name}`, error)
      result[getKeyName(name)] = null
    }
  }
  return result
}
//
export async function downloadAlliedStarFilesbyFormat(
  caseCode: string,
  filesFormat: string[],
  dispatch?: any,
) {
  dispatch(setData({ loading: "pending" }))
  const response = await getAlliedCaseDetail(caseCode)
  if (response.status != 200) return {}

  const attachments = response?.data?.data?.attachments || []
  const result: { [key: string]: File | null } = {}

  const matchedFiles = attachments.filter((att) =>
    filesFormat.includes(att.fileFormat.toLowerCase()),
  )

  for (const matched of matchedFiles) {
    const url = matched.url
    const name = matched.fileName
    try {
      dispatch(setData({ loading: "pending" }))
      const fetchRes = await fetch(url)
      if (!fetchRes.ok) {
        throw new Error(`Download failed: ${name} (${fetchRes.status})`)
      }

      const blob = await fetchRes.blob()
      const file = new File([blob], name, {
        type: blob.type || "application/octet-stream",
      })

      result[getKeyName(name)] = file
    } catch (error) {
      console.error(`Error downloading file ${name}`, error)
      result[getKeyName(name)] = null
    }
  }
  dispatch(setData({ loading: "succeeded" }))
  console.log(result)
  return result
}

export async function loginAlliedStar(
  redirectUrl: string = "",
  outUid: string = "",
  notifyUrl: string = "",
) {
  const authorizationPage = `https://asconnect-us.allied-star.com/third/v2/auth-login?appid=${appId}&redirectUrl=${redirectUrl}&outUid=${outUid}&notifyUrl=${notifyUrl}`
  window.location.href = authorizationPage
}
